/**
 * A URL antiga `/parceiros?partner=` continua sendo aceita como entrada — links
 * e QR codes já distribuídos apontam para ela — mas só como porta de entrada: a
 * rota canônica do parceiro é a página `/parceiros/{identificador}`.
 */
export type PartnerLegacySearch = {
  partner?: string
}

export type PartnerLike = {
  id: number
  slug?: string
  uid?: string
  name?: string
}

export const validatePartnerLegacySearch = (
  search: Record<string, unknown>
): PartnerLegacySearch => {
  const { partner } = search

  if (typeof partner === 'string' && partner.trim()) {
    return { partner: partner.trim() }
  }

  if (typeof partner === 'number' && !Number.isNaN(partner)) {
    return { partner: String(partner) }
  }

  return {}
}

/** Identificador vindo da URL antiga, ou `undefined` quando ela não foi usada. */
export const legacyPartnerParam = (search: PartnerLegacySearch) =>
  search.partner?.trim() ? search.partner.trim() : undefined

export const buildPartnerPageHref = (identifier: string | number) =>
  `/parceiros/${encodeURIComponent(String(identifier))}`

export const findPartnerByParam = (
  partners: PartnerLike[],
  param: string | undefined
): PartnerLike | undefined => {
  if (!param?.trim()) return undefined

  const trimmed = param.trim()
  const numericId = Number(trimmed)

  if (!Number.isNaN(numericId) && String(numericId) === trimmed) {
    return partners.find(partner => partner.id === numericId)
  }

  return partners.find(
    partner =>
      partner.slug === trimmed ||
      partner.uid === trimmed ||
      String(partner.id) === trimmed
  )
}

/** Identificador aceito pelo show da API (`find_by: :slug`). */
export const partnerApiIdentifier = (partner: PartnerLike) =>
  partner.slug || partner.uid || String(partner.id)

/**
 * O param da rota pode chegar como id, uid ou slug — deep links de bônus,
 * blocker e QR code carregam o que o payload deles tinha à mão —, mas o show só
 * resolve por slug. A tradução usa o bundle da gamificação já em cache.
 *
 * Devolve `null` enquanto o bundle não chegou: pedir `/partners/1192` com id
 * numérico responderia 404, e é melhor esperar o bundle do que renderizar um
 * erro que se resolve sozinho um instante depois. Com o bundle carregado e sem
 * o parceiro nele, o param crua segue para a API — quem responde é ela.
 */
export const resolvePartnerShowIdentifier = (
  partners: PartnerLike[],
  param: string
): string | null => {
  const partner = findPartnerByParam(partners, param)
  if (partner) return partnerApiIdentifier(partner)

  if (!partners.length) return null

  return param
}
